/**
 * Shared child-profile utilities for the Birch tracker pages.
 *
 * Exports a single global: window.BirchChildren
 *
 * Usage:
 *   BirchChildren.load()          → Promise<Child[]>
 *   BirchChildren.upsert(child)   → Promise<Child[]>
 *   BirchChildren.remove(id)      → Promise<Child[]>
 *   BirchChildren.pickChild(opts) → Promise<Child|null>
 *     opts: { title, onPick(child) }
 *     Shows an overlay to pick from multiple children, or resolves immediately if 0 or 1 child.
 */

'use strict';

(function () {
  var API = '/apps/ai/children';

  function apiFetch(path, options) {
    return fetch(API + (path || ''), Object.assign({
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    }, options || {})).then(function (r) { return r.json(); });
  }

  function newId() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  }

  /* ── Core API ─────────────────────────────────────────────────────────────── */

  function load() {
    return apiFetch('').then(function (r) { return r.children || []; }).catch(function () { return []; });
  }

  function upsert(child) {
    return apiFetch('', {
      method: 'POST',
      body: JSON.stringify({ action: 'upsert', child: child })
    }).then(function (r) { return r.children || []; });
  }

  function remove(id) {
    return apiFetch('', {
      method: 'POST',
      body: JSON.stringify({ action: 'remove', child: { id: id } })
    }).then(function (r) { return r.children || []; });
  }

  /* ── Child picker overlay ────────────────────────────────────────────────── */

  function pickChild(opts) {
    opts = opts || {};
    return load().then(function (children) {
      if (children.length === 0) return Promise.resolve(null);
      if (children.length === 1) return Promise.resolve(children[0]);

      // Multiple children — show picker
      return new Promise(function (resolve) {
        var overlay = document.createElement('div');
        overlay.className = 'bc-picker-overlay';
        overlay.innerHTML =
          '<div class="bc-picker-sheet">' +
            '<div class="bc-picker-title">' + (opts.title || 'Who is this for?') + '</div>' +
            '<div class="bc-picker-list">' +
              children.map(function (c) {
                var dob = c.dob ? new Date(c.dob) : null;
                var age = dob ? calcAgeLabel(dob) : '';
                return '<button class="bc-picker-btn" data-id="' + c.id + '">' +
                  '<span class="bc-picker-btn__name">' + escHtml(c.name) + '</span>' +
                  (age ? '<span class="bc-picker-btn__age">' + age + '</span>' : '') +
                '</button>';
              }).join('') +
            '</div>' +
            '<button class="bc-picker-cancel">Cancel</button>' +
          '</div>';

        document.body.appendChild(overlay);

        overlay.querySelectorAll('.bc-picker-btn').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var id = btn.dataset.id;
            var chosen = children.find(function (c) { return c.id === id; });
            document.body.removeChild(overlay);
            resolve(chosen || null);
          });
        });

        overlay.querySelector('.bc-picker-cancel').addEventListener('click', function () {
          document.body.removeChild(overlay);
          resolve(null);
        });
      });
    });
  }

  /* ── Helpers ─────────────────────────────────────────────────────────────── */

  function escHtml(s) {
    return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  function calcAgeLabel(dob) {
    var now = new Date();
    var diffMs = now - dob;
    var weeks  = Math.floor(diffMs / (7   * 24 * 3600 * 1000));
    var months = Math.floor(diffMs / (30.44 * 24 * 3600 * 1000));
    var years  = Math.floor(diffMs / (365.25 * 24 * 3600 * 1000));
    if (months < 1)   return weeks + ' week' + (weeks === 1 ? '' : 's') + ' old';
    if (months < 24)  return months + ' month' + (months === 1 ? '' : 's') + ' old';
    return years + ' year' + (years === 1 ? '' : 's') + ' old';
  }

  /**
   * pickContext — shows a bottom-sheet picker for a flat list of TrackerContext objects.
   * A TrackerContext has: { type:'own'|'shared', label, childName, childId, ownerId, ownerName }
   * Resolves with the chosen context, or null if cancelled.
   *
   * @param {TrackerContext[]} contexts
   * @param {{ title?: string }} opts
   * @returns {Promise<TrackerContext|null>}
   */
  function pickContext(contexts, opts) {
    opts = opts || {};
    if (!contexts || contexts.length === 0) return Promise.resolve(null);
    if (contexts.length === 1) return Promise.resolve(contexts[0]);

    return new Promise(function (resolve) {
      var overlay = document.createElement('div');
      overlay.className = 'bc-picker-overlay';
      overlay.innerHTML =
        '<div class="bc-picker-sheet">' +
          '<div class="bc-picker-title">' + escHtml(opts.title || 'Whose tracker?') + '</div>' +
          '<div class="bc-picker-list">' +
            contexts.map(function (ctx, i) {
              var badge  = ctx.type === 'shared'
                ? '<span class="bc-picker-btn__badge">Shared</span>'
                : '';
              var sub    = ctx.type === 'shared' && ctx.ownerName
                ? '<span class="bc-picker-btn__age">' + escHtml(ctx.ownerName) + '\'s child</span>'
                : (ctx.dob ? '<span class="bc-picker-btn__age">' + calcAgeLabel(new Date(ctx.dob)) + '</span>' : '');
              return '<button class="bc-picker-btn" data-idx="' + i + '">' +
                '<span class="bc-picker-btn__name">' + escHtml(ctx.childName || ctx.label || 'Child') + '</span>' +
                sub + badge +
              '</button>';
            }).join('') +
          '</div>' +
          '<button class="bc-picker-cancel">Cancel</button>' +
        '</div>';

      document.body.appendChild(overlay);

      overlay.querySelectorAll('.bc-picker-btn[data-idx]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var idx = parseInt(btn.dataset.idx, 10);
          document.body.removeChild(overlay);
          resolve(contexts[idx] || null);
        });
      });

      overlay.querySelector('.bc-picker-cancel').addEventListener('click', function () {
        document.body.removeChild(overlay);
        resolve(null);
      });
    });
  }

  /* ── Expose global ───────────────────────────────────────────────────────── */
  window.BirchChildren = { load: load, upsert: upsert, remove: remove, pickChild: pickChild, pickContext: pickContext, newId: newId, calcAgeLabel: calcAgeLabel };
})();
